import {assert} from 'assert-ts';
import type {Schemas} from 'domainTypes/dto';
import {type WorkPrizeWon} from 'services/prizes/types';
import {
  mapAgentRoleDto,
  mapToAgentRoleListDto,
} from 'services/agent/mappers/mapAgentRoleDto';
import {mapToWorkRelationsDto} from 'services/agent/mappers/mapRelationsDto';
import {mapLiteraturePrizeAgent, mapLiteraturePrizeLink} from 'services/prizes';
import {
  mapToVerifiedDto,
  removeEmptyStringValues,
  unique,
} from 'services/utils';
import {CodeListRef} from 'schemas/references';
import type {WorkV4} from '../types';
import {mapWorkRelationsDto} from './mapWorkRelationsDto';

export const mapWorkBaseDto = (dto: Schemas.WorkBaseDto): WorkV4 => {
  const {
    id,
    created,
    modified,
    agents,
    workRelations,
    agentRelations,
    seriesRelations,
    preferredTitle,
    originalTitle,
    titleInfo,
    type,
    generalNote,
    prizes,
    themes,
    norwegianSubjects,
    ...rest
  } = dto;
  const links = mapWorkRelationsDto({
    workRelations,
    agentRelations,
    seriesRelations,
  });

  return {
    id: assert(id, 'mapWorkBaseDto: id expected', dto),
    created: assert(created, 'mapWorkBaseDto: created expected', dto),
    modified: assert(modified, 'mapWorkBaseDto: modified expected', dto),
    ...rest,
    agents: (agents ?? []).map(mapAgentRoleDto),
    links,
    preferredTitle: titleInfo?.preferredTitle ?? preferredTitle ?? [],
    originalTitle: titleInfo?.originalTitle ?? originalTitle,
    otherTitles: titleInfo?.otherTitles.sort((a, b) => a.order - b.order) ?? [],
    type: type ?? CodeListRef.WORK_TYPE.Bok,
    generalNote: removeEmptyStringValues(generalNote),
    prizes: mapWorkPrizesWonDto(prizes),
    themes: themes ? unique(themes) : undefined,
    norwegianSubjects: norwegianSubjects
      ? unique(norwegianSubjects)
      : undefined,
  };
};
export const mapWorkPrizesWonDto = (
  dto: Schemas.WorkPrizeWon[] | undefined,
): WorkPrizeWon[] => {
  return (dto ?? [])
    .map(mapWorkPrizeWonDto)
    .sort((a, b) => (b.year ?? 0) - (a.year ?? 0));
};
export const mapWorkPrizeWonDto = ({
  agent: looseAgent,
  prize: loosePrize,
  type: looseType,
  year: looseYear,
}: Schemas.WorkPrizeWon): WorkPrizeWon => {
  const agent = assert(looseAgent, 'mapWorkPrizeWonDto: agent must be defined');
  const prize = assert(loosePrize, 'mapWorkPrizeWonDto: prize must be defined');
  const type = assert(looseType, 'mapWorkPrizeWonDto: type must be defined');
  const year = assert(looseYear, 'mapWorkPrizeWonDto: year must be defined');

  return {
    year,
    type,
    agent: {link: mapLiteraturePrizeAgent(agent)},
    prize: {link: mapLiteraturePrizeLink(prize)},
  };
};
export const mapToWorkBaseDto = (data: WorkV4): Schemas.WorkBaseDto => {
  const {
    agents,
    links,
    preferredTitle,
    originalTitle,
    otherTitles,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    prizes, //  Not updated
    ...rest
  } = data;
  const relations = mapToWorkRelationsDto(links);

  return {
    ...rest,
    agents: mapToAgentRoleListDto(agents),
    titleInfo: {
      preferredTitle: preferredTitle ?? [],
      originalTitle,
      otherTitles:
        otherTitles?.map((oT, i) => ({
          ...oT,
          order: i,
        })) ?? [],
    },
    prizes: [],
    ...relations,
    verified: mapToVerifiedDto(data.id, data.verified),
  };
};
